import React, { forwardRef, HTMLAttributes } from 'react';
import { useTheme } from '../layout/ThemeProvider';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'cosmic' | 'minimal';
  hover?: boolean;
  glow?: boolean;
  children: React.ReactNode;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(({
  variant = 'default',
  hover = true,
  glow = false,
  className = '',
  children,
  ...props
}, ref) => {
  const { isDark } = useTheme();

  const getCardClasses = () => {
    const baseClasses = 'glass-card';
    
    const variantClasses = {
      default: 'p-3 md:p-6',
      cosmic: 'p-3 md:p-8 card-cosmic',
      minimal: 'p-2 md:p-4',
    };

    const interactiveClasses = hover ? 'hover-lift' : '';
    const glowClasses = glow ? 'pulse-glow' : '';

    return `${baseClasses} ${variantClasses[variant]} ${interactiveClasses} ${glowClasses} ${className}`;
  };

  return (
    <div
      ref={ref}
      className={getCardClasses()}
      {...props}
    >
      {children}
    </div>
  );
});

Card.displayName = 'Card';

// Card Header Component
export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  space?: boolean;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(({
  className = '',
  children,
  space = true,
  ...props
}, ref) => {
  const spacingClasses = space ? 'mb-3 md:mb-6' : '';
  
  return (
    <div
      ref={ref}
      className={`${spacingClasses} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
});

CardHeader.displayName = 'CardHeader';

// Card Title Component
export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  gradient?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(({
  as: Component = 'h3',
  gradient = false,
  icon,
  className = '',
  children,
  ...props
}, ref) => {
  const { isDark } = useTheme();
  
  const textClasses = gradient ? 'text-gradient' : '';
  const sizeClasses = {
    h1: 'text-2xl',
    h2: 'text-xl',
    h3: 'text-lg',
    h4: 'text-base',
    h5: 'text-sm',
    h6: 'text-xs',
  };

  return (
    <Component
      ref={ref}
      className={`font-semibold ${sizeClasses[Component]} ${textClasses} ${className}`}
      style={{
        color: gradient ? undefined : 'var(--space-text-primary)',
      }}
      {...props}
    >
      {icon && (
        <span className="inline-flex items-center mr-2">
          {icon}
        </span>
      )}
      {children}
    </Component>
  );
});

CardTitle.displayName = 'CardTitle';

// Card Content Component
export interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(({
  className = '',
  children,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={`text-base leading-relaxed ${className}`}
      style={{ color: 'var(--space-text-secondary)' }}
      {...props}
    >
      {children}
    </div>
  );
});

CardContent.displayName = 'CardContent';

// Card Footer Component
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right' | 'between';
  space?: boolean;
}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(({
  className = '',
  children,
  align = 'left',
  space = true,
  ...props
}, ref) => {
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };
  
  const spacingClasses = space ? 'mt-3 pt-2 md:mt-6 md:pt-4' : '';
  const borderClasses = space ? 'border-t' : '';

  return (
    <div
      ref={ref}
      className={`flex items-center ${alignClasses[align]} ${spacingClasses} ${borderClasses} ${className}`}
      style={{
        borderColor: space ? 'var(--space-border)' : undefined,
      }}
      {...props}
    >
      {children}
    </div>
  );
});

CardFooter.displayName = 'CardFooter';

// Specialized Space-themed Card Variants
export interface SpaceCardProps extends CardProps {
  constellation?: 'orion' | 'cassiopeia' | 'ursa';
  planet?: boolean;
}

export const SpaceCard = forwardRef<HTMLDivElement, SpaceCardProps>(({
  constellation,
  planet = false,
  className = '',
  children,
  ...props
}, ref) => {
  const { isDark } = useTheme();

  return (
    <Card
      ref={ref}
      variant="cosmic"
      className={`relative overflow-hidden ${className}`}
      {...props}
    >
      {/* Constellation Background Pattern */}
      {constellation && (
        <div className="absolute top-2 right-2 opacity-20 pointer-events-none">
          <div className="w-8 h-8 flex items-center justify-center">
            {constellation === 'orion' && (
              <svg viewBox="0 0 24 24" className="w-full h-full">
                <circle cx="6" cy="6" r="1" fill="currentColor" />
                <circle cx="18" cy="6" r="1" fill="currentColor" />
                <circle cx="12" cy="12" r="1" fill="currentColor" />
                <circle cx="6" cy="18" r="1" fill="currentColor" />
                <circle cx="18" cy="18" r="1" fill="currentColor" />
              </svg>
            )}
            {constellation === 'cassiopeia' && (
              <svg viewBox="0 0 24 24" className="w-full h-full">
                <circle cx="4" cy="8" r="1" fill="currentColor" />
                <circle cx="8" cy="4" r="1" fill="currentColor" />
                <circle cx="12" cy="8" r="1" fill="currentColor" />
                <circle cx="16" cy="4" r="1" fill="currentColor" />
                <circle cx="20" cy="8" r="1" fill="currentColor" />
              </svg>
            )}
            {constellation === 'ursa' && (
              <svg viewBox="0 0 24 24" className="w-full h-full">
                <circle cx="4" cy="6" r="1" fill="currentColor" />
                <circle cx="8" cy="4" r="1" fill="currentColor" />
                <circle cx="12" cy="6" r="1" fill="currentColor" />
                <circle cx="16" cy="8" r="1" fill="currentColor" />
                <circle cx="20" cy="6" r="1" fill="currentColor" />
                <circle cx="12" cy="12" r="1" fill="currentColor" />
                <circle cx="8" cy="16" r="1" fill="currentColor" />
              </svg>
            )}
          </div>
        </div>
      )}
      
      {/* Distant Planet Decoration */}
      {planet && (
        <div 
          className="absolute -top-4 -right-4 w-16 h-16 rounded-full opacity-10 pointer-events-none"
          style={{
            background: isDark 
              ? 'radial-gradient(circle at 30% 30%, rgba(123, 93, 153, 0.6) 0%, rgba(26, 15, 43, 0.8) 100%)'
              : 'radial-gradient(circle at 30% 30%, rgba(99, 102, 241, 0.4) 0%, rgba(168, 85, 247, 0.2) 100%)',
          }}
        />
      )}
      
      {children}
    </Card>
  );
});

SpaceCard.displayName = 'SpaceCard';

export default Card; 